import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Link from 'flarum/common/components/Link';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import ExhibitionEditModal from './ExhibitionEditModal';
import formatDate from '../utils/formatDate';
import { promptLogin, loginRequiredPlaceholder } from '../utils/requireLogin';
/**
 * /exhibitions - the one remaining entry point for both sociétés and their
 * expositions (SocietiesListPage was folded in here, see its own git
 * history) - one section per société the actor can access
 * (SocietyResource::scope() already only returns those, same condition
 * ExhibitionResource's own groupId field checks on create, so every
 * société listed here is one the actor can add an exhibition to), each
 * showing that société's own exhibitions nested underneath, with its own
 * "gérer"/"ajouter une exposition" actions right in the section's own
 * colored header. Société management (toxicités/cartothèque/mise en page
 * des cartes) stays its own page at /societies/:id, which also has its own
 * "ajouter une exposition" entry point in its own Expositions section -
 * both this list and that page can create one, deliberately not just one
 * or the other.
 */
export default class ExhibitionsListPage extends Page {
  societies = null;
  exhibitions = null;
  error = false;
  requiresLogin = false;
  oninit(vnode) {
    super.oninit(vnode);
    app.setTitle(app.translator.trans('champis-exhibitions.forum.list_page.title'));
    this.societies = null;
    this.exhibitions = null;
    this.error = false;
    this.requiresLogin = false;
    if (!app.session.user) {
      // Auto-prompt on arrival - a guest reaching this URL directly (a
      // bookmark, a shared link, search) almost certainly wants to see
      // what's behind it, not read an explanation first. loginRequiredPlaceholder()
      // below is the fallback for if they dismiss this without logging in.
      promptLogin();
    }
    this.load();
  }
  load() {
    this.societies = null;
    this.exhibitions = null;
    this.error = false;
    if (!app.session.user) {
      // Every société fetch below is ->authenticated() (SocietyResource) -
      // skip the doomed request entirely rather than showing this page's
      // generic "impossible de charger" error, indistinguishable from a
      // real failure (see requireLogin.js's own docblock).
      this.requiresLogin = true;
      return;
    }
    this.requiresLogin = false;
    Promise.all([app.store.find('champis-exhibitions-societies', {
      sort: 'name'
    }),
    // page[limit]: this page groups *every* société's exhibitions together
    // client-side (see exhibitionsFor()) - a plain find() defaults to
    // page[limit]=20 (ExhibitionResource's own ->paginate(20, 500)), which
    // silently dropped the oldest exhibitions once the site-wide total grew
    // past 20 (confirmed: Ajoie's 2009 exposition, the very oldest, went
    // missing this way). 500 is that same resource's own configured max.
    app.store.find('champis-exhibitions', {
      sort: '-startDate',
      page: {
        limit: 500
      }
    })]).then(_ref => {
      let [societies, exhibitions] = _ref;
      this.societies = societies;
      this.exhibitions = exhibitions;
      m.redraw();
    }).catch(() => {
      this.error = true;
      m.redraw();
    });
  }
  view() {
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-exhibitions.forum.list_page.${key}`, params);
    };
    return m("div", {
      className: "Page ChampisExhibitionsListPage"
    }, m("div", {
      className: "container"
    }, this.requiresLogin ? loginRequiredPlaceholder(t('login_required')) : this.error ? m(Placeholder, {
      text: t('error')
    }) : this.societies === null || this.exhibitions === null ? m(LoadingIndicator, null) : this.societies.length === 0 ? m(Placeholder, {
      text: t('empty')
    }) : this.societies.map(society => this.societySection(society))));
  }
  societySection(society) {
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-exhibitions.forum.list_page.${key}`, params);
    };
    const exhibitions = this.exhibitionsFor(society);
    return m("div", {
      className: "ChampisSocietyPage-section",
      key: society.id()
    }, m("div", {
      className: "ChampisSocietyPage-sectionHeader"
    }, m("h3", null, m(Link, {
      href: app.route('society', {
        id: society.id()
      })
    }, society.name())), m("div", {
      className: "ChampisSocietyPage-sectionHeaderActions"
    }, m(Link, {
      href: `${app.route('cartotheque', {
        id: society.id()
      })}?from=list`,
      className: "Button"
    }, m("i", {
      className: "icon fas fa-address-card",
      "aria-hidden": "true"
    }), " ", t('cartotheque_button')), m(Link, {
      href: app.route('society', {
        id: society.id()
      }),
      className: "Button"
    }, t('manage_society_button')), m(Button, {
      className: "Button Button--primary",
      icon: "fas fa-plus",
      onclick: () => this.openCreateModal(society)
    }, t('new_button')))), m("div", {
      className: "ChampisSocietyPage-sectionBody"
    }, exhibitions.length === 0 ? m(Placeholder, {
      text: t('empty_society')
    }) : m("ul", {
      className: "ChampisDescriptionsList-items"
    }, exhibitions.map(exhibition => this.row(exhibition)))));
  }
  exhibitionsFor(society) {
    return this.exhibitions.filter(exhibition => String(exhibition.groupId()) === String(society.groupId()));
  }
  row(exhibition) {
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-exhibitions.forum.list_page.${key}`, params);
    };
    return m("li", {
      key: exhibition.id()
    }, m(Link, {
      href: app.route('exhibition', {
        id: exhibition.id()
      }),
      className: "ChampisDescriptionsList-item"
    }, m("span", {
      className: "ChampisDescriptionsList-name"
    }, exhibition.name()), m("div", {
      className: "ChampisDescriptionsList-author"
    }, this.dateRange(exhibition), exhibition.location() && ` — ${exhibition.location()}`, ' — ', t('findings_count', {
      count: exhibition.findingsCount() || 0
    }))));
  }
  dateRange(exhibition) {
    const start = exhibition.startDate();
    const end = exhibition.endDate();
    return end && end !== start ? `${formatDate(start)} → ${formatDate(end)}` : formatDate(start);
  }
  openCreateModal(society) {
    app.modal.show(ExhibitionEditModal, {
      society,
      onsaved: () => this.load()
    });
  }
}
flarum.reg.add('champis-exhibitions', 'forum/components/ExhibitionsListPage', ExhibitionsListPage);